import { RcFile } from 'antd/lib/upload/interface';
import imageCompression from 'browser-image-compression';

const DEFAULT_MAX_IMAGE_FILE_SIZE = 1 * 1024 * 1024; // 1 MB
const DEFAULT_MAX_PDF_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export interface FileValidatorOptions {
  maxFileSizeBytes?: number;
  maxWidthOrHeight?: number;
  permittedContentTypes?: string[];
}

export interface FileValidatorResult {
  success: boolean;
  file?: RcFile;
  message?: string;
  code?: string;
}

export class FileValidator {
  private file: RcFile;
  private readonly maxFileSizeBytes: number;
  private readonly maxWidthOrHeight: number | undefined;
  private readonly permittedContentTypes: string[];

  constructor(file: RcFile, options?: FileValidatorOptions) {
    this.file = file;
    if (this.file.type === "application/pdf") {
      this.maxFileSizeBytes = options?.maxFileSizeBytes ?? DEFAULT_MAX_PDF_FILE_SIZE;
    } else {
      this.maxFileSizeBytes = DEFAULT_MAX_IMAGE_FILE_SIZE;
    }
    this.maxWidthOrHeight = options?.maxWidthOrHeight;
    this.permittedContentTypes = options?.permittedContentTypes ?? [];
  }

  public async validate(): Promise<FileValidatorResult> {
    if (!this.validateContentType()) {
      return {
        success: false,
        message: `Invalid content type: ${this.file.type}`,
        code: 'content-type'
      };
    }

    const result = await this.validateContentLength();

    if (!result.success) {
      return {
        success: false,
        message: result.message,
        code: result.code
      };
    }

    return result;
  }

  private validateContentType(): boolean {
    if (this.permittedContentTypes.length == 0) {
      return true;
    }

    return this.permittedContentTypes.includes(this.file.type);
  }

  private async validateContentLength(): Promise<FileValidatorResult> {
    if (this.file.type.startsWith('image/')) {
      try {
        const newFile = await this.compressImage();
        if (newFile.size <= this.maxFileSizeBytes) {
          return {
            success: true,
            file: newFile
          };
        } else {
          return {
            success: false,
            message: `Invalid content length: ${newFile.size}. Max size permitted: ${this.maxFileSizeBytes}`,
            code: 'content-length'
          };
        }
      } catch (error) {
        console.error('cannot compress:', error);
        return {
          success: false,
          message: `Cannot compress file: ${error}`,
          code: 'compress'
        };
      }
    }

    if (this.file.size <= this.maxFileSizeBytes) {
      return {
        success: true,
        file: this.file
      };
    } else {
      return {
        success: false,
        message: `Invalid content length: ${this.file.size}. Max size permitted: ${this.maxFileSizeBytes}`,
        code: 'content-length'
      };
    }
  }

  private async compressImage(): Promise<RcFile> {
    console.log('beforeCompression size:', this.file.size);
    let options: any = {};
    if (this.maxFileSizeBytes) {
      options.maxSizeMB = this.maxFileSizeBytes / 1024 / 1024;
    }
    if (this.maxWidthOrHeight) {
      options.maxWidthOrHeight = this.maxWidthOrHeight;
    }
    console.log('beforeCompression options:', options);
    const {uid} = this.file;
    const newFile = (await imageCompression(this.file, options)) as RcFile;
    newFile.uid = uid;
    console.log('afterCompression size:', newFile.size);
    return newFile;
  }
}
